<script setup lang="ts">
  import _ from "lodash";
  import { useElementBounding } from "@vueuse/core";
  import { TALK_ROOM_DM_MAX } from "~/consts/TalkConfig";
  import type { ContractMember } from "~/entities/ContractMember";
  import { PlanInfo } from "~/entities/PlanInfo";
  import type { TalkRoom } from "~/entities/TalkRoom";
  import { TalkRoomEditInfo } from "~/entities/TalkRoomEditInfo";
  import { usePlanStore } from "~/stores/PlanStore";
  import { useTalkRoomStore } from "~/stores/TalkRoomStore";
  import { useSnackBarStore } from "~/stores/SnackBarStore";
  import { useTalkRoomEditStore } from "~/stores/TalkRoomEditStore";
  import { useHistoryStore } from "~/stores/HistoryStore";
  const historyStore = useHistoryStore();

  interface Props {
    planInfo: PlanInfo;
    talkRooms: TalkRoom[];
    isOpen: boolean;
    isTest?: boolean; // テスト時のみ true にする。でないと v-dialog を find できないため
  }
  const props = defineProps<Props>();

  interface Emits {
    (event: "update:isOpen", isOpen: boolean): void;
  }
  const emit = defineEmits<Emits>();
  const computedIsOpen = computed<boolean>({
    get: () => props.isOpen,
    set: value => {
      emit("update:isOpen", value);
    },
  });

  const planId = props.planInfo.id;

  const headerAreaRef = ref<HTMLElement | null>(null);
  const footerAreaRef = ref<HTMLElement | null>(null);
  const { height: headerAreaElementHeight } = useElementBounding(headerAreaRef);
  const { height: footerAreaElementHeight } = useElementBounding(footerAreaRef);
  const listAreaElementHeight = computed(() => {
    return (
      document.body.clientHeight - // 画面全体の高さを使うのが最も安定したので採用
      headerAreaElementHeight.value -
      footerAreaElementHeight.value -
      70
    ); // 実績ベースで margin・padding を差し引いた
  });

  // メンバー取得
  const page = ref(1);
  const PAGE_SIZE = 20;
  const planStore = usePlanStore();
  const members = ref<ContractMember[]>([]);
  const isLast = ref(false); // 最後までロードしたかどうか
  let isLoading = false;
  const load = async ({ done }: any) => {
    if (isLast.value) {
      done("empty");
      return;
    }
    if (isLoading) {
      // load 連発防止
      done("ok");
      return;
    }
    isLoading = true;
    try {
      const options = {
        // 入会日降順
        sort: "desc",
        sort_item: "start_date",
        user_name: searchWord.value,
        plan_id: planId,
        only_active: true, // 表示はアクティブメンバーのみ
      };
      await planStore.fetchMembers(page.value, PAGE_SIZE, options);
      const newMembers = planStore.contractMemberResponse?.contractMembers || [];

      // TODO: member の有効期限チェック。API とつなぎ込み安定動作確認後に削除する
      // https://fincs.backlog.com/view/FINCS-2694
      for (const member of newMembers) {
        if (member.endDate !== null && member.endDate < FormatDateString(new Date(), "yyyy-MM-dd")) {
          // only_active 指定しているのに無効なメンバーが返ってきているのはおかしいのでエラーとさせる
          throw new Error("inactive member found");
        }
      }

      members.value = [...members.value, ...newMembers];
      if (newMembers.length === PAGE_SIZE) {
        done("ok");
        page.value++;
      } else {
        isLast.value = true;
        done("empty");
      }
    } catch (error: any) {
      snackBarStore.setError(`会員の取得に失敗しました`);
      sentryErrorLog(`TalkDmUserSearchModal: failed to fetch members: ${error} ${error.message}`);
      done("error");
    } finally {
      isLoading = false;
    }
  };

  // 検索
  const searchWord = ref("");
  const doSearch = () => {
    selectedUserId.value = "";
    page.value = 1;
    members.value = [];
    isLast.value = false;
    load({ done: () => {} });
  };
  const debouncedSearch = _.debounce(doSearch, props.isTest ? 1 : 1000); // 1秒デバウンスして検索。テスト時は即時検索
  watch(searchWord, () => {
    if (!canSearchTalk(searchWord.value)) {
      return;
    }
    debouncedSearch();
  });

  const getDmTalkRoom = (userId: string) => {
    return props.talkRooms.find(talkRoom => talkRoom.dmUser?.userId === userId);
  };

  const hasDmTalkRoom = (userId: string) => {
    return !!getDmTalkRoom(userId);
  };

  // DM する
  const selectedUserId = ref("");
  const selectedUserName = computed(() => {
    const member = members.value.find(member => member.clientUser.userId === selectedUserId.value);
    return member?.clientUser.userName || "";
  });
  const talkRoomStore = useTalkRoomStore();
  const talkRoomEditStore = useTalkRoomEditStore();
  const snackBarStore = useSnackBarStore();
  const onMemberClick = (userId: string) => {
    selectedUserId.value = userId;
  };
  const onDoDm = async () => {
    if (!selectedUserId.value) {
      return;
    }

    const dmTalkRoom = getDmTalkRoom(selectedUserId.value);
    if (dmTalkRoom) {
      // 既存のダイレクトメッセージへ
      historyStore.to(`/plan/${planId}?tab=talk&room=${dmTalkRoom.talkRoomId}`);
      computedIsOpen.value = false;
    } else if (talkRoomStore.isMaxDMTalkRooms()) {
      snackBarStore.setError(`ダイレクトメッセージは最大${TALK_ROOM_DM_MAX}個までです`);
    } else {
      // 新規ダイレクトメッセージ作成
      try {
        const editInfo = TalkRoomEditInfo.forDM(selectedUserId.value, selectedUserName.value);
        const newTalkRoomId = await talkRoomEditStore.createTalkRoom(planId, editInfo);
        snackBarStore.set(`ダイレクトメッセージを作成しました`);
        // talkRoomStore.talkRooms を更新して他のコンポーネントがリアクティブに動くように
        await talkRoomStore.getTalkRooms(planId);
        historyStore.to(`/plan/${planId}?tab=talk&room=${newTalkRoomId}`);
        computedIsOpen.value = false;
      } catch (error: any) {
        snackBarStore.setError(`ダイレクトメッセージの作成に失敗しました`);
        sentryErrorLog(`TalkDmUserSearchModal: failed to createTalkRoom: ${error}`);
      }
    }
  };

  // レスポンシブ対応
  const isSp = isSpDisplayRef();
</script>

<template>
  <v-dialog
    v-model="computedIsOpen"
    class="mx-0"
    :width="isSp ? '100%' : '70%'"
    height="100%"
    scrim="black"
    :attach="!!isTest"
  >
    <v-card class="d-flex flex-column justify-center dialog-body" :class="[isSp ? 'pt-8 pb-4' : 'py-4']">
      <div class="close-button" @click="computedIsOpen = false">✕</div>

      <div ref="headerAreaRef" class="dialog-header">
        <div class="talk-dm-user-search-title text-center">講座内DMを作る・探す</div>
        <div class="search-wrapper">
          <v-text-field
            v-model="searchWord"
            class="search-input"
            placeholder="ユーザー名を入力してください"
            prepend-inner-icon="$mdiMagnify"
            variant="solo"
            autofocus
            @keydown.enter.prevent
          ></v-text-field>
        </div>
      </div>

      <v-card-text class="dialog-list">
        <v-radio-group :model-value="selectedUserId">
          <v-infinite-scroll class="user-list" :height="listAreaElementHeight" @load="load">
            <div
              v-for="(memberInfo, index) in members"
              :key="index"
              class="talk-dm-user"
              @click="onMemberClick(memberInfo.clientUser.userId)"
            >
              <div class="d-flex align-center">
                <v-radio :value="memberInfo.clientUser.userId" class="ml-1 ml-sm-2 mr-2"></v-radio>
                <div style="position: relative; width: 30px" class="mr-2">
                  <div
                    v-if="hasDmTalkRoom(memberInfo.clientUser.userId)"
                    style="position: absolute; width: 100px; top: -20px"
                    class="text-blue text-left text-caption"
                  >
                    ✔ DM
                  </div>
                  <AtomsUserIcon :src="memberInfo.clientUser.userImage" :width="30" :height="30" />
                </div>
                <div class="mx-2">
                  <div class="text-left text-caption text-value">
                    {{ omitString(memberInfo.clientUser.fullName, isSp ? 16 : 1000) }}
                  </div>
                  <div class="text-left text-value">{{ memberInfo.clientUser.userName }}</div>
                </div>
              </div>
            </div>
            <template #empty>
              <div class=""></div>
            </template>
          </v-infinite-scroll>
        </v-radio-group>
      </v-card-text>

      <v-card-actions ref="footerAreaRef" class="dialog-footer">
        <div class="d-flex justify-center">
          <AtomsButton
            class="my-4"
            :class="[isSp ? 'mx-4' : 'mx-8']"
            :width="isSp ? 120 : 200"
            color="white"
            style-bg-color="#119CF6"
            :disabled="selectedUserId === ''"
            data-testid="notification-setting-save"
            @click="onDoDm"
            >DMする</AtomsButton
          >
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
  .dialog-body {
    height: 100%;
    color: white;
    background-color: #222222;
  }
  .close-button {
    position: absolute;
    top: 0;
    right: 0;
    color: white;
    margin-top: 20px;
    margin-right: 20px;
    cursor: pointer;
    z-index: 1;
  }
  .dialog-header {
    position: absolute;
    top: 20px;
    width: 100%;
    padding: 0 20px;
  }
  .dialog-list {
    position: absolute;
    top: 140px;
    width: 100%;
    padding: 0 8px !important;
  }
  .dialog-footer {
    position: absolute;
    bottom: 0;
    width: 100%;
    justify-content: center;
  }
  .talk-dm-user-search-title {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.8em;
    margin-bottom: 20px;
  }
  .talk-dm-user {
    height: 80px;
    padding: 20px;
    margin: 0px 0 0px 0;
    cursor: pointer;
    display: flex;
    &:hover {
      background-color: rgb(var(--v-theme-monochrome-05));
    }
  }
</style>

<style lang="scss">
  .v-dialog > .v-overlay__content {
    margin-left: 0;
    margin-right: 0;
    max-width: 100%;
  }
</style>
